import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@/store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiUserLine, RiPhoneLine, RiBuildingLine, RiShieldUserLine, RiAlertLine, RiArrowRightLine, RiLinkM } from '@remixicon/react';

// Simple Label component
const Label = ({ children, className, htmlFor }) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 ${className || ''}`}>
    {children}
  </label>
);

export default function ProfileSetup({ isProfile = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'user',
    orgId: '',
  });
  const { setAuthType } = useAuthStore();

  const { data: existingUser, isLoading: isCheckingUser } = useQuery({
    queryKey: ['user', 'BY_USER_ID', user?.id],
    queryFn: () => apiClient.getUserByUserId(user.id),
    enabled: !!user?.id,
    retry: false,
  });

  useEffect(() => {
    if (existingUser) {
      setFormData({
        firstName: existingUser.first_name || '',
        lastName: existingUser.last_name || '',
        phone: existingUser.phone || '',
        role: existingUser.role || 'user',
        orgId: existingUser.org_id || '',
      });
    }
  }, [existingUser]);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;
    let sessionChecked = false;

    const getCurrentUser = async () => {
      // Check for OAuth errors in URL
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const oauthError = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

      if (oauthError) {
        console.error('OAuth error detected:', oauthError, errorDescription);
        setError(`Authentication failed: ${decodeURIComponent(errorDescription || oauthError)}`);
        // Don't try to get session if there's an OAuth error
        setTimeout(() => {
          if (mounted) 
            setAuthType('signup');
            navigate('/signup');
        }, 5000);
        return;
      }

      // Listen for auth state changes to handle OAuth callback
      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (!mounted) return;

        console.log('ProfileSetup auth event:', event, 'Session exists:', !!session, 'User:', session?.user?.email);

        // Handle SIGNED_IN event from OAuth callback
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('User signed in via OAuth:', session.user.email);
          setUser(session.user);
          sessionChecked = true;
        }
        // Only redirect to signin if we're truly signed out and not during OAuth processing
        else if (event === 'SIGNED_OUT' && sessionChecked) {
          console.log('User signed out, redirecting to signin');
          setAuthType('signin');
          navigate('/signin');
        }
      });

      authSubscription = data.subscription;

      // Check for existing session after a delay to allow OAuth hash processing
      setTimeout(async () => {
        if (!mounted || sessionChecked) return;

        console.log('Checking for existing session...');
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
        }

        console.log('Session check result:', !!session, 'User:', session?.user?.email);

        if (session?.user) {
          setUser(session.user);
          sessionChecked = true;
        } else {
          console.log('No session found, redirecting to signin');
          setAuthType('signin');
          navigate('/signin');
        }
      }, 1000); // Increased timeout to 1 second for OAuth processing
    };

    getCurrentUser();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate]);

  useEffect(() => {
    // Fetch organizations list
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await apiClient.getOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Failed to fetch organizations:', err);
        setError('Failed to load organizations. Please refresh the page.');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {

      // If user is already registered, skip registration
      if (existingUser) {
        await useAuthStore.getState().fetchUserProfile();

        // update user info
        await apiClient.updateUser(existingUser.userprofile_id, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          role: formData.role,
          org_id: formData.orgId,
        });

        navigate('/dashboard');
        return;
      }

      // Register user in the gtj schema
      await apiClient.registerUser({
        user_id: user.id,
        email: user.email,
        org_id: formData.orgId,
        role: formData.role,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        status: 'NEW'
      });

      // Fetch the updated user profile to update the store
      await useAuthStore.getState().fetchUserProfile();

      // Profile completed, navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Profile setup error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex ${!isProfile ? "items-center justify-center" : ""} py-4`}>
      <div className="max-w-md w-full space-y-8 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
        <div>
          {!isProfile &&
          <h2 className="text-3xl font-bold text-primary-500 mb-2">Complete Your Profile</h2>
          }
          <p className="text-gray-600">
            Please provide some additional information to complete your registration.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <RiAlertLine className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Profile Setup Error
                </h3>
                <div className="mt-1 text-sm text-red-700">
                  {error}
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {isCheckingUser ? (
            <div className="space-y-6 animate-pulse">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="h-4 w-20 bg-gray-200 rounded" />
                  <div className="h-11 bg-gray-200 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-20 bg-gray-200 rounded" />
                  <div className="h-11 bg-gray-200 rounded-lg" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-28 bg-gray-200 rounded" />
                <div className="h-11 bg-gray-200 rounded-lg" />
              </div>
              <div className="space-y-2">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-11 bg-gray-200 rounded-lg" />
              </div>
              <div className="pt-4">
                <div className="h-11 bg-gray-200 rounded-lg" />
              </div>
            </div>
          ) : (
          <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <RiUserLine size={16} />
                </span>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:none"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <RiUserLine size={16} />
                </span>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <RiPhoneLine size={16} />
              </span>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Enter your phone number (optional)"
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="orgId">Organization <span className="text-red-500">*</span></Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
                <RiBuildingLine size={16} />
              </span>
              <select
                id="orgId"
                name="orgId"
                value={formData.orgId}
                onChange={handleChange}
                required
                disabled={loadingOrgs || loading}
                className="h-11 w-full pl-10 pr-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
              >
                <option value="">
                  {loadingOrgs ? 'Loading organizations...' : 'Select your organization'}
                </option>
                {organizations.map((org) => (
                  <option key={org.organization_id} value={org.organization_id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/*<div className="space-y-2">
            <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10">
                <RiShieldUserLine size={16} />
              </span>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                disabled={true}
                className="h-11 w-full pl-10 pr-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="operator">Operator</option>
                <option value="broker">Broker</option>
              </select>
            </div>
          </div>*/}

          <div className="flex flex-col space-y-3 pt-4">
            <Button
              type="submit"
              disabled={loading || !formData.firstName || !formData.lastName || !formData.orgId}
              className="w-full h-11 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {!isProfile ? 'Continue' : 'Save'}
                  {!isProfile && 
                  <RiArrowRightLine className="ml-2 h-4 w-4" />
                  }
                </div>
              )}
            </Button>
          </div>
          </>
          )}
        </form>
      </div>
    </div>
  );
}